// Waiting-room display board (FR-QUE-4). Open on a TV or tablet; updates live.
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@powersync/react';
import { useApp } from '@/app/state';
import { toMyanmarDigits } from '@/lib/myanmar/digits';

interface Row { session_id: string; doctor_name_en: string | null; doctor_name_my: string | null; room: string | null; current: string | null; next1: string | null; next2: string | null; next3: string | null }

export function BoardPage() {
  const { t, i18n } = useTranslation();
  const my = i18n.language === 'my';
  const { session, clinic } = useApp();
  const { data } = useQuery<Row>(
    `select s.id as session_id, d.name_en as doctor_name_en, d.name_my as doctor_name_my, s.room,
      (select token_label from tokens t where t.session_id = s.id and t.status in ('called','in_consultation') order by t.called_at desc limit 1) as current,
      (select token_label from tokens t where t.session_id = s.id and t.status = 'waiting' order by t.priority desc, t.issued_at limit 1) as next1,
      (select token_label from tokens t where t.session_id = s.id and t.status = 'waiting' order by t.priority desc, t.issued_at limit 1 offset 1) as next2,
      (select token_label from tokens t where t.session_id = s.id and t.status = 'waiting' order by t.priority desc, t.issued_at limit 1 offset 2) as next3
     from sessions s join doctors d on d.id = s.doctor_id
     where s.clinic_id = ? and s.session_date = date('now','localtime') and s.status = 'open' order by s.start_time`, [session?.clinicId ?? '']);

  // Chime when the current token changes (optional audio call).
  const last = useRef<string>('');
  useEffect(() => {
    const sig = (data ?? []).map((r) => r.current).join('|');
    if (last.current && sig !== last.current) {
      try { const ac = new AudioContext(); const o = ac.createOscillator(); o.frequency.value = 880; o.connect(ac.destination); o.start(); o.stop(ac.currentTime + 0.25); } catch { /* no audio */ }
    }
    last.current = sig;
  }, [data]);

  const L = (s: string | null) => (s ? (my ? toMyanmarDigits(s) : s) : '—');
  return (
    <div className={`min-h-dvh bg-ink p-6 text-white ${my ? 'font-my' : ''}`}>
      <header className="mb-6 flex items-baseline justify-between">
        <h1 className="text-3xl font-bold">{(my && clinic?.name_my) || clinic?.name}</h1>
        <Clock />
      </header>
      <div className="grid gap-6 md:grid-cols-2">
        {(data ?? []).map((r) => (
          <section key={r.session_id} className="rounded-2xl bg-white/5 p-6">
            <div className="flex items-baseline justify-between">
              <h2 className="text-2xl font-bold">{my ? (r.doctor_name_my ?? r.doctor_name_en) : (r.doctor_name_en ?? r.doctor_name_my)}</h2>
              {r.room && <span className="text-xl text-white/70">{t('queue.room')} {my ? toMyanmarDigits(r.room) : r.room}</span>}
            </div>
            <div className="mt-4 grid grid-cols-[2fr_1fr] gap-4">
              <div className="rounded-xl bg-accent p-4 text-center">
                <div className="text-sm uppercase tracking-widest text-white/80">{t('queue.nowServing')}</div>
                <div className="text-8xl font-bold leading-none">{L(r.current)}</div>
              </div>
              <div className="rounded-xl bg-white/10 p-4">
                <div className="text-sm uppercase tracking-widest text-white/70">{t('queue.upNext')}</div>
                <div className="mt-1 text-4xl font-bold leading-tight">{[r.next1, r.next2, r.next3].filter(Boolean).map((x) => L(x)).join('  ') || '—'}</div>
              </div>
            </div>
          </section>
        ))}
        {(data ?? []).length === 0 && <div className="text-2xl text-white/60">{t('queue.noSessions')}</div>}
      </div>
    </div>
  );
}

function Clock() {
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const id = setInterval(() => { if (ref.current) ref.current.textContent = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }, 1000);
    return () => clearInterval(id);
  }, []);
  return <span ref={ref} className="text-3xl tabular-nums text-white/80" />;
}
